import { ApolloError, ServerError } from '@apollo/client';
import { getEkardoApolloClient } from '@netfront/common-library';
import {
  GET_CONTENT_PAGE_BY_URL_QUERY,
  IGetContentPageByUrlQueryGraphQLResponse,
  IGetContentPageByUrlQueryVariables,
  ISearchContentGroupsQueryGraphQLResponse,
  ISearchContentGroupsQueryVariables,
  SEARCH_CONTENT_GROUPS_QUERY,
} from '@netfront/ekardo-content-library';
import { GetStaticPathsResult, NextPage, GetStaticProps } from 'next';

import { ContentPage, ContentPageProps, STATIC_PAGE_REGENERATION_TIME_IN_SECONDS } from '../components';
import { IStaticPropsContextParams } from '../interfaces';

const ContentNextPage: NextPage<ContentPageProps> = ({ contentPage }) => {
  return <ContentPage contentPage={contentPage} />;
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  if (!process.env.REACT_APP_EKARDO_API_URL) {
    return { paths: [], fallback: 'blocking' };
  }

  try {
    const client = getEkardoApolloClient({
      product: 'EKARDO',
    });

    const {
      data: {
        contentGroup: { searchContentGroups },
      },
    } = await client.query<ISearchContentGroupsQueryGraphQLResponse, ISearchContentGroupsQueryVariables>({
      query: SEARCH_CONTENT_GROUPS_QUERY,
      variables: {
        projectId: String(process.env.REACT_APP_PROJECT_ID),
        type: 'PAGE',
      },
    });

    // getContentPageByUrl only returns published pages, so prerendering any other status fails the build
    const contentPages = searchContentGroups
      .map(({ contentPages: groupContentPages }) => groupContentPages)
      .flat()
      .filter(({ status }) => status === 'PUBLISHED');

    const paths = contentPages.map(({ url: pageUrl }) => ({
      params: {
        pageUrl,
      },
    }));

    return {
      paths,
      fallback: 'blocking',
    };
  } catch {
    return { paths: [], fallback: 'blocking' };
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { pageUrl } = params as IStaticPropsContextParams;

  try {
    const client = getEkardoApolloClient({
      product: 'EKARDO',
    });

    const {
      data: {
        contentPage: { getContentPageByUrl: contentPage },
      },
    } = await client.query<IGetContentPageByUrlQueryGraphQLResponse, IGetContentPageByUrlQueryVariables>({
      query: GET_CONTENT_PAGE_BY_URL_QUERY,
      variables: {
        contentPageUrl: String(pageUrl),
        projectId: String(process.env.REACT_APP_PROJECT_ID),
      },
    });

    return {
      props: {
        contentPage,
      },
      revalidate: STATIC_PAGE_REGENERATION_TIME_IN_SECONDS,
    };
  } catch (error) {
    // The API answers 400 for unpublished or missing pages. Rethrow anything else so ISR keeps the last good page and retries
    const { statusCode } = ((error as ApolloError).networkError ?? {}) as Partial<ServerError>;

    if (statusCode !== 400) {
      throw error;
    }

    return { notFound: true, revalidate: STATIC_PAGE_REGENERATION_TIME_IN_SECONDS };
  }
};

// eslint-disable-next-line import/no-default-export
export default ContentNextPage;
